import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Camera, Upload, Plus, Trash2, Pencil, ArrowLeft, Receipt, FolderOpen, AlertTriangle, Building2, ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import { useConfirm } from "@/hooks/useConfirm";
import { findBusinessDuplicate } from "@/lib/businessDedupe";
import JobPicker from "@/components/dashboard/JobPicker";
import { PINNED_PROJECTS } from "@/components/dashboard/pinnedProjects";
import ReceiptViewer from "@/components/dashboard/ReceiptViewer";
import { useAuth } from "@/context/AuthContext";

const money = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const curMonthStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

/**
 * Overhead card — hourly labour billed to the pinned Office / Estimating
 * pseudo-jobs this month, with a More/Less itemization per employee-week.
 */
const OverheadCard = () => {
  const [month, setMonth] = useState(curMonthStr);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: d } = await api.get(`/expenses/overhead?month=${month}`);
        if (alive) setData(d);
      } catch { if (alive) setData(null); }
    })();
    return () => { alive = false; };
  }, [month]);
  const [y, m] = month.split("-").map(Number);
  const label = new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const shift = (n) => {
    const d = new Date(y, m - 1 + n, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };
  return (
    <div className="mb-3 rounded-lg border border-white/10 bg-white/[0.02]" data-testid="overhead-card">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5 md:px-4">
        <Building2 className="h-4 w-4 shrink-0 text-[#8fa4f0]" />
        <div className="min-w-0 flex-1 basis-44">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Overhead · office &amp; estimating labour</p>
          <p className="font-mono text-lg font-bold text-white" data-testid="overhead-total">{money(data?.total || 0)}</p>
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/40" data-testid="overhead-split">
            Office {money(data?.office_total || 0)} · Estimating {money(data?.estimating_total || 0)}
          </p>
        </div>
        {/* On narrow phones this nav row drops to its own full-width line
            instead of crushing the title into letter-by-letter wrapping. */}
        <div className="flex items-center gap-1.5 max-[520px]:w-full max-[520px]:justify-between">
          <div className="flex items-center gap-1.5">
            <button onClick={() => shift(-1)} aria-label="Previous month" data-testid="overhead-prev-month"
              className="rounded-md border border-white/15 bg-white/5 p-1.5 text-white/70 hover:border-white/25 hover:text-white">
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <span className="min-w-[110px] text-center font-mono text-[11px] uppercase tracking-widest text-white/60" data-testid="overhead-month">{label}</span>
            <button onClick={() => shift(1)} disabled={month >= curMonthStr()} aria-label="Next month" data-testid="overhead-next-month"
              className="rounded-md border border-white/15 bg-white/5 p-1.5 text-white/70 hover:border-white/25 hover:text-white disabled:cursor-not-allowed disabled:opacity-30">
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
          <button onClick={() => setOpen((v) => !v)} data-testid="overhead-toggle" aria-expanded={open}
            className="ml-1 inline-flex items-center gap-1 rounded-md border border-white/15 bg-white/5 px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest text-white/70 hover:border-white/25 hover:text-white">
            {open ? "Less" : "More"} {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="border-t border-white/[0.06]" data-testid="overhead-items">
          {(data?.rows || []).length === 0 && (
            <p className="px-4 py-4 text-center font-mono text-[11px] uppercase tracking-widest text-white/40">
              No Office / Estimating hours this month
            </p>
          )}
          {(data?.rows || []).map((r, i) => (
            <div key={`${r.employee_id}-${r.week_start}-${r.location}`} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 border-b border-white/5 px-4 py-2 last:border-0" data-testid={`overhead-row-${i}`}>
              <span className="text-sm font-semibold text-white">{r.name}</span>
              <span className="rounded border border-[#1a3fd1]/40 bg-[#1a3fd1]/10 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-[#a9baf5]">{r.location}</span>
              <span className="font-mono text-[10px] text-white/40">
                wk of {new Date(`${r.week_start}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              </span>
              <span className="font-mono text-[11px] text-white/60">{r.hours.toFixed(2)}h × {money(r.rate)}</span>
              <span className="ml-auto font-mono text-xs font-semibold text-emerald-300">{money(r.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const emptyForm = { name: "", description: "", vendor: "", vendor_id: "", total: "", date: "", receipt_image: null };

const ExpenseDialog = ({ open, onClose, projects, defaultProjectId, assignedOwner, onSaved, editing }) => {
  const [projectId, setProjectId] = useState(defaultProjectId || "");
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [vendorByName, setVendorByName] = useState({}); // lowercase vendor name -> vendor_id
  const vendorDup = useMemo(() => findBusinessDuplicate(form.vendor, businesses), [form.vendor, businesses]);
  useEffect(() => {
    if (!open) return;
    // Suggestions come from the Vendors section first, plus any business names
    // typed into past expenses that aren't vendors yet.
    Promise.all([api.get("/vendors"), api.get("/expenses/businesses")])
      .then(([v, b]) => {
        const names = new Map(); // lowercase -> display name
        const map = {};
        (Array.isArray(v.data) ? v.data : []).forEach((x) => {
          if (!x.name) return;
          names.set(x.name.trim().toLowerCase(), x.name);
          map[x.name.trim().toLowerCase()] = x.vendor_id;
        });
        (Array.isArray(b.data) ? b.data : []).forEach((n) => {
          if (n && !names.has(n.trim().toLowerCase())) names.set(n.trim().toLowerCase(), n);
        });
        setBusinesses([...names.values()].sort((a, z) => a.localeCompare(z)));
        setVendorByName(map);
      })
      .catch(() => setBusinesses([]));
  }, [open]);

  useEffect(() => {
    if (open) {
      if (editing) {
        setProjectId(editing.project_id || defaultProjectId || "");
        setForm({
          name: editing.name || "",
          description: editing.description || "",
          vendor: editing.vendor || "",
          vendor_id: editing.vendor_id || "",
          total: editing.total != null ? String(editing.total) : "",
          date: editing.date || "",
          receipt_image: editing.receipt_image || null,
        });
      } else {
        setProjectId(defaultProjectId || "");
        setForm({ ...emptyForm, date: new Date().toLocaleDateString("en-CA") });
      }
    }
  }, [open, defaultProjectId, editing]);

  const pickFile = (accept, capture) => {
    const inp = document.createElement("input");
    inp.type = "file";
    inp.accept = accept;
    if (capture) inp.capture = capture;
    inp.onchange = () => {
      const f = inp.files?.[0];
      if (!f) return;
      const r = new FileReader();
      r.onload = () => setForm((s) => ({ ...s, receipt_image: String(r.result) }));
      r.readAsDataURL(f);
    };
    inp.click();
  };

  const save = async () => {
    if (!projectId) return toast.error("Pick a project first");
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const body = {
        project_id: projectId,
        name: form.name.trim(),
        description: form.description.trim(),
        vendor: form.vendor.trim(),
        vendor_id: form.vendor_id || "",
        owner: assignedOwner || "",
        total: parseFloat(form.total) || 0,
        date: form.date || "",
        receipt_image: form.receipt_image || null,
      };
      if (editing?.expense_id) {
        await api.put(`/expenses/${editing.expense_id}`, body);
        toast.success("Expense updated");
      } else {
        await api.post("/expenses", body);
        toast.success("Expense saved");
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save expense");
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="expense-dialog">
      <div className="w-full max-w-lg rounded-lg border border-white/10 bg-[#0b0b0f] p-4 text-white">
        <h3 className="text-lg font-bold uppercase tracking-tight">{editing ? "Edit expense" : "Add expense"}</h3>
        <div className="mt-3 space-y-3">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-white/50">Project</label>
            <div className="mt-1">
              <JobPicker
                value={projectId}
                onChange={setProjectId}
                projects={projects}
                pinned={PINNED_PROJECTS}
                placeholder="Select a project…"
                testid="expense-project-picker"
                allowClear={false}
                className="h-9 text-sm"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Expense" v={form.name} onChange={(v) => setForm((s) => ({ ...s, name: v }))} testid="expense-name" />
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-white/50">Business</label>
              <input
                value={form.vendor}
                onChange={(ev) => setForm((s) => ({ ...s, vendor: ev.target.value, vendor_id: vendorByName[ev.target.value.trim().toLowerCase()] || "" }))}
                data-testid="expense-vendor"
                placeholder="Where did you spend it?"
                list="expense-businesses"
                autoComplete="off"
                className="mt-1 h-9 w-full rounded border border-white/15 bg-white/5 px-2 text-sm text-white focus:border-[#1a3fd1] focus:outline-none"
              />
              <datalist id="expense-businesses">
                {businesses.map((b) => (<option key={b} value={b} />))}
              </datalist>
              {vendorDup && (
                <p
                  data-testid="expense-vendor-dup"
                  className="mt-1 flex items-center gap-1.5 rounded border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[11px] text-amber-100"
                >
                  <AlertTriangle className="h-3 w-3 shrink-0" />
                  Looks like <span className="font-semibold">{vendorDup}</span> already —
                  <button
                    type="button"
                    onClick={() => setForm((s) => ({ ...s, vendor: vendorDup }))}
                    data-testid="expense-vendor-dup-apply"
                    className="ml-1 rounded border border-amber-400/70 bg-amber-400/20 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest text-white hover:bg-amber-400/30"
                  >
                    Use existing
                  </button>
                </p>
              )}
            </div>
          </div>
          <Field label="Description" v={form.description} onChange={(v) => setForm((s) => ({ ...s, description: v }))} testid="expense-desc" area />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-white/50">Total $</label>
              <input
                type="text"
                inputMode="decimal"
                value={form.total}
                onChange={(ev) => setForm((s) => ({ ...s, total: ev.target.value.replace(/[^0-9.]/g, "") }))}
                placeholder="0.00"
                data-testid="expense-total"
                className="mt-1 h-9 w-full rounded border border-white/15 bg-white/5 px-2 font-mono text-sm text-white focus:border-[#1a3fd1] focus:outline-none"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-white/50">Date</label>
              <input
                type="date"
                value={form.date}
                onChange={(ev) => setForm((s) => ({ ...s, date: ev.target.value }))}
                data-testid="expense-date"
                className="mt-1 h-9 w-full rounded border border-white/15 bg-white/5 px-2 text-sm text-white focus:border-[#1a3fd1] focus:outline-none"
              />
            </div>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-white/50">Receipt (optional)</label>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <button type="button" onClick={() => pickFile("image/*", "environment")}
                data-testid="expense-capture"
                className="inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/5 px-3 py-2 text-xs text-white/80 hover:border-white/25">
                <Camera className="h-3.5 w-3.5" /> Take photo
              </button>
              <button type="button" onClick={() => pickFile("image/*,application/pdf", null)}
                data-testid="expense-upload"
                className="inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/5 px-3 py-2 text-xs text-white/80 hover:border-white/25">
                <Upload className="h-3.5 w-3.5" /> Upload file
              </button>
              {form.receipt_image && (
                <span className="font-mono text-[10px] text-emerald-800 dark:text-emerald-300">✓ receipt attached</span>
              )}
            </div>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-md border border-white/15 bg-white/5 px-3 py-2 text-xs text-white/70" data-testid="expense-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="rounded-md bg-[#1a3fd1] px-4 py-2 text-xs font-semibold text-white hover:bg-[#1a3fd1]/85" data-testid="expense-save">
            {saving ? "Saving…" : "Save expense"}
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, v, onChange, testid, area }) => (
  <div>
    <label className="font-mono text-[10px] uppercase tracking-widest text-white/50">{label}</label>
    {area ? (
      <textarea rows={2} value={v} onChange={(ev) => onChange(ev.target.value)} data-testid={testid}
        className="mt-1 w-full rounded border border-white/15 bg-white/5 px-2 py-1.5 text-sm text-white focus:border-[#1a3fd1] focus:outline-none" />
    ) : (
      <input value={v} onChange={(ev) => onChange(ev.target.value)} data-testid={testid}
        className="mt-1 h-9 w-full rounded border border-white/15 bg-white/5 px-2 text-sm text-white focus:border-[#1a3fd1] focus:outline-none" />
    )}
  </div>
);

const ExpensesTab = () => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [selectedId, setSelectedId] = useState(null); // project_id or "all"
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [dialogProject, setDialogProject] = useState("");
  const [receiptSrc, setReceiptSrc] = useState(null);
  const [selected, setSelected] = useState(new Set()); // expense_ids checked for bulk delete
  const confirm = useConfirm();

  const load = useCallback(async () => {
    try {
      const [p, e] = await Promise.all([
        api.get("/projects"),
        api.get("/expenses"),
      ]);
      setProjects(p.data || []);
      setExpenses(e.data || []);
    } catch { toast.error("Could not load expenses"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Deep link: /dashboard?tab=expenses&expense=<id> (from a project's billing
  // rows) — open that expense's editor once the list has it.
  const [searchParams] = useSearchParams();
  const expenseLinkDone = useRef(null);
  useEffect(() => {
    const target = searchParams.get("expense");
    if (!target || expenseLinkDone.current === target || !expenses.length) return;
    const found = expenses.find((x) => x.expense_id === target);
    if (found) {
      expenseLinkDone.current = target;
      setSelectedId(found.project_id || "all");
      setEditingExpense(found);
      setDialogOpen(true);
    }
  }, [searchParams, expenses]);

  const activeProjects = useMemo(
    () => projects.filter((p) => (p.status || "Active") === "Active" || p.status === "In Progress"),
    [projects],
  );

  const projectTotals = useMemo(() => {
    const m = {};
    expenses.forEach((x) => { m[x.project_id] = (m[x.project_id] || 0) + Number(x.total || 0); });
    return m;
  }, [expenses]);

  const projectCounts = useMemo(() => {
    const m = {};
    expenses.forEach((x) => { m[x.project_id] = (m[x.project_id] || 0) + 1; });
    return m;
  }, [expenses]);

  const rowsForSelected = useMemo(() => {
    if (!selectedId) return [];
    if (selectedId === "all") return expenses;
    return expenses.filter((x) => x.project_id === selectedId);
  }, [selectedId, expenses]);

  const projectName = (pid) => projects.find((p) => p.project_id === pid)?.name || pid;

  // PO-first labels so the dropdown answers "which job is this for?" at a glance
  const pickerProjects = useMemo(
    () => activeProjects.map((p) => ({ ...p, name: p.po ? `${p.po} — ${p.name}` : p.name })),
    [activeProjects],
  );

  const visibleIds = rowsForSelected.map((x) => x.expense_id);
  const selectedCount = visibleIds.filter((id) => selected.has(id)).length;
  const allChecked = visibleIds.length > 0 && selectedCount === visibleIds.length;

  const toggleAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      visibleIds.forEach((id) => (allChecked ? next.delete(id) : next.add(id)));
      return next;
    });

  const toggleOne = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const reassign = async (x, pid) => {
    if ((x.project_id || "") === pid) return;
    try {
      await api.patch(`/expenses/${x.expense_id}/project`, { project_id: pid });
      toast.success(pid ? `Moved to ${projectName(pid)}` : "Removed from project");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not move expense");
    }
  };

  const removeSelected = async () => {
    const ids = visibleIds.filter((id) => selected.has(id));
    if (!ids.length) return;
    const ok = await confirm({
      title: `Delete ${ids.length} selected expense${ids.length === 1 ? "" : "s"}?`,
      description: "This cannot be undone.",
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      const r = await api.post("/expenses/bulk-delete", { ids });
      toast.success(`Deleted ${r.data.deleted} expense${r.data.deleted === 1 ? "" : "s"}`);
      setSelected(new Set());
      load();
    } catch {
      toast.error("Could not delete selected expenses");
    }
  };

  const removeExpense = async (x) => {
    const ok = await confirm({ title: `Delete "${x.name}"?`, confirmLabel: "Delete", danger: true });
    if (!ok) return;
    try { await api.delete(`/expenses/${x.expense_id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  return (
    <div className="space-y-4 p-4 sm:p-6" data-testid="expenses-tab">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8fa4f0]">Expenses</p>
          <h2 className="text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl">Company Expenses</h2>
        </div>
        <button
          onClick={() => { setDialogProject(selectedId && selectedId !== "all" ? selectedId : ""); setDialogOpen(true); }}
          data-testid="expense-add-btn"
          className="inline-flex items-center gap-1.5 rounded-md bg-[#1a3fd1] px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white hover:bg-[#1a3fd1]/85"
        >
          <Plus className="h-4 w-4" /> Add expense
        </button>
      </div>

      {selectedId === null ? (
        <>
          {/* C-1c: overhead itemizes employee hours x pay rate — payroll data */}
          {user?.permissions?.payroll && <OverheadCard />}
          {/* Compact list — one row per project (matches ProjectsTab layout).
              "Everything" pill sits at the top so it's still the fast path. */}
          <div className="overflow-hidden rounded-lg border border-white/10 bg-white/[0.02]" data-testid="expenses-project-list">
            <button
              onClick={() => setSelectedId("all")}
              data-testid="expenses-view-all"
              className="group relative flex w-full items-center gap-2 border-b border-white/[0.06] bg-[#1a3fd1]/[0.08] px-3 py-2 text-left hover:bg-[#1a3fd1]/[0.16] md:px-4"
            >
              <Receipt className="h-4 w-4 shrink-0 text-[#8fa4f0]" />
              <span className="flex min-w-0 flex-1 items-baseline justify-between gap-2">
                <span className="flex min-w-0 flex-col md:flex-row md:items-baseline md:gap-3">
                  <span className="text-sm font-semibold text-white">All expenses</span>
                  <span className="truncate font-mono text-[10px] uppercase tracking-widest text-white/50">
                    {expenses.length} items
                  </span>
                </span>
                <span className="shrink-0 font-mono text-xs font-semibold text-emerald-300">
                  {money(expenses.reduce((s, x) => s + Number(x.total || 0), 0))}
                </span>
              </span>
            </button>
            {activeProjects.map((p) => {
              const cnt = projectCounts[p.project_id] || 0;
              const total = projectTotals[p.project_id] || p.materials_cost || 0;
              return (
                <button
                  key={p.project_id}
                  onClick={() => setSelectedId(p.project_id)}
                  data-testid={`expenses-project-${p.project_id}`}
                  className="group relative flex w-full items-center gap-2 border-b border-white/[0.05] px-3 py-2 text-left transition-colors hover:bg-white/[0.04] md:px-4"
                >
                  <FolderOpen className="h-4 w-4 shrink-0 text-white/40 group-hover:text-[#8fa4f0]" />
                  <span className="flex min-w-0 flex-1 items-baseline justify-between gap-2">
                    <span className="flex min-w-0 flex-col md:flex-row md:items-baseline md:gap-3">
                      <span className="truncate text-sm font-medium text-white/90">{p.name}</span>
                      <span className="truncate font-mono text-[10px] uppercase tracking-widest text-white/40">
                        {(p.customer || "—")} · <span data-testid={`expense-count-${p.project_id}`}>{cnt} {cnt === 1 ? "expense" : "expenses"}</span>
                      </span>
                    </span>
                    <span className="shrink-0 font-mono text-xs font-semibold text-emerald-300/90">
                      {money(total)}
                    </span>
                  </span>
                </button>
              );
            })}
            {activeProjects.length === 0 && (
              <p className="p-6 text-center text-sm text-white/50">No active projects yet.</p>
            )}
          </div>
        </>
      ) : (
        <>
          <button
            onClick={() => setSelectedId(null)}
            data-testid="expenses-back"
            className="inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/5 px-3 py-1.5 text-xs text-white/70 hover:border-white/25 hover:text-white"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> All projects
          </button>
          <h3 className="text-lg font-semibold text-white">
            {selectedId === "all" ? "All expenses" : projectName(selectedId)}
          </h3>
          {rowsForSelected.length === 0 ? (
            <p className="rounded-lg border border-white/10 p-6 text-center text-sm text-white/50">No expenses yet.</p>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2" data-testid="expenses-bulk-bar">
                <label className="flex cursor-pointer items-center gap-2 text-xs text-white/70">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    onChange={toggleAll}
                    data-testid="expenses-select-all"
                    className="h-4 w-4 accent-[#1a3fd1]"
                  />
                  Select all{selectedCount > 0 ? ` (${selectedCount} selected)` : ""}
                </label>
                {selectedCount > 0 && (
                  <button
                    onClick={removeSelected}
                    data-testid="expenses-delete-selected"
                    className="inline-flex items-center gap-1.5 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-1.5 text-xs font-semibold text-red-300 hover:border-red-500/60"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete selected ({selectedCount})
                  </button>
                )}
              </div>
            {/* overflow-visible so a row's project dropdown never gets clipped */}
            <div className="rounded-lg border border-white/10">
              {rowsForSelected.map((x) => (
                <div key={x.expense_id} data-testid={`expense-row-${x.expense_id}`}
                  className="flex flex-wrap items-center justify-between gap-3 border-b border-white/5 px-4 py-3 text-sm">
                  <input
                    type="checkbox"
                    checked={selected.has(x.expense_id)}
                    onChange={() => toggleOne(x.expense_id)}
                    data-testid={`expense-check-${x.expense_id}`}
                    className="h-4 w-4 shrink-0 accent-[#1a3fd1]"
                    aria-label="Select expense"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                      {selectedId === "all" ? projectName(x.project_id) + " · " : ""}{x.vendor || "no vendor"}
                    </p>
                    <p className="font-medium text-white/90">{x.name}</p>
                    {x.description && <p className="text-xs text-white/50">{x.description}</p>}
                  </div>
                  <div className="w-44 shrink-0 sm:w-56">
                    <JobPicker
                      value={x.project_id || ""}
                      onChange={(pid) => reassign(x, pid)}
                      projects={pickerProjects}
                      pinned={PINNED_PROJECTS}
                      placeholder="— project —"
                      testid={`expense-project-${x.expense_id}`}
                      allowClear
                      className="h-8 text-xs"
                    />
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-base text-white">{money(x.total)}</p>
                    <p className="font-mono text-[9px] uppercase tracking-widest text-white/30">
                      {new Date(x.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  {x.receipt_image && (
                    <button
                      type="button"
                      onClick={() => setReceiptSrc(x.receipt_image)}
                      data-testid={`expense-receipt-${x.expense_id}`}
                      className="rounded border border-white/15 bg-white/5 px-2 py-1 text-[10px] font-mono text-emerald-800 dark:text-emerald-300 hover:border-white/25"
                    >
                      view receipt
                    </button>
                  )}
                  <button
                    onClick={() => { setEditingExpense(x); setDialogOpen(true); }}
                    data-testid={`expense-edit-${x.expense_id}`}
                    className="rounded border border-white/15 bg-white/5 p-1.5 text-white/70 hover:border-white/25 hover:text-white"
                    title="Edit expense"
                    aria-label="Edit"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => removeExpense(x)} data-testid={`expense-del-${x.expense_id}`}
                    className="rounded border border-red-500/30 bg-red-500/5 p-1.5 text-red-400 hover:border-red-500/50">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
            </>
          )}
        </>
      )}

      <ExpenseDialog
        open={dialogOpen}
        editing={editingExpense}
        onClose={() => { setDialogOpen(false); setEditingExpense(null); }}
        projects={pickerProjects}
        defaultProjectId={dialogProject}
        assignedOwner={user?.assigned_owner || ""}
        onSaved={load}
      />
      <ReceiptViewer src={receiptSrc} onClose={() => setReceiptSrc(null)} />
    </div>
  );
};

export default ExpensesTab;
